/* eslint-disable no-console */
// @TODO MOST OF THE UTILS BELOW NEED TO BE REFACTORED AT SOME POINT
/* eslint func-names: ["error", "as-needed"] */

import { AuthUtils, CloudActions, CloudConstants } from '@portal/portal-core';
import Cookies from 'js-cookie';
import Lockr from 'utils/local-store';

// `Config` has no path alias for TS; resolve the untyped .js module relatively (imports as `any`).
import { serverUrl } from '../../Config';
// utils is an untyped .js import; dismissConnectionAlert/setValueInLocalStorage resolve as `any`.
import { dismissConnectionAlert, setValueInLocalStorage } from 'utils';
import { getClientConfigAsync } from '../../middlewares/cloud-middlewares';
import {
  applicationSyncPollCycleStorageKey,
  authTokenKeyStorageKey,
  clientConfigCheckCycleStorageKey,
  envStorageKey,
  userSyncPollCycleStorageKey,
} from './constants';
import * as AuthClientSelectors from './selectors';

/** A single error entry returned by the API. */
interface ApiErrorEntry {
  errorCode?: string;
  errorText?: string;
  url?: string;
}

/** The subset of an API response shape accessed by the helpers below. */
interface ApiResponse {
  errors: ApiErrorEntry[];
  result: { userId?: string };
}

/** A user object as stored in the users slice. */
interface UserObj {
  firstname: string;
  lastname: string;
}

/** The slice of the store that holds keyed user objects. */
interface UsersState {
  users?: Record<string, UserObj>;
}

/** The email verification slice of the store accessed by the selectors. */
interface EmailVerificationSlice {
  uiState?: string;
  userId?: string;
  emailAddress?: string;
}

/** Minimal Redux store surface used by the polling / sync helpers. */
interface AuthReduxStore {
  getState: () => { emailVerification: EmailVerificationSlice };
  dispatch: (action: unknown) => unknown;
}

/** The subset of the client-config poll response shape accessed below. */
interface ClientConfigApiResponse {
  code: number;
  data: string;
}

const responseHasErrors = (response: ApiResponse): boolean =>
  Array.isArray(response.errors) && response.errors.length > 0;

export const createSignInPayload = (
  email: string,
  password: string,
): { userEmail: string; userPassword: string } => ({
  userEmail: email,
  userPassword: password,
});

export const createProductInfoHeader = (productName: string): void => {
  const productInfoHeader: Record<string, string> = {};
  productInfoHeader[CloudConstants.CLIENT_HEADERS.productName] = productName;
};

export const getErrorCodeFromApiResponse = (response: ApiResponse): string | null => {
  if (responseHasErrors(response) && response.errors[0].errorCode) {
    return response.errors[0].errorCode;
  }
  return null;
};

export const getErrorTextFromApiResponse = (response: ApiResponse): string | null => {
  if (responseHasErrors(response) && response.errors[0].errorText) {
    return response.errors[0].errorText;
  }
  return null;
};

export const getUserIdFromApiResponse = (response: ApiResponse): string | null => {
  if (response.result.userId) {
    return response.result.userId;
  }
  return null;
};

export const getErrorUrlFromApiResponse = (response: ApiResponse): string | null => {
  if (response.errors[0].url) {
    return response.errors[0].url;
  }
  return null;
};

/**
 * generates initials from a user object
 * <p>
 * @param {Object} state - the current state of the store
 * @param {string} userObj - the user object to extract first and last initials from
 * @returns {string} - user initials
 */
export const getInitialsFromUserObj = function (userObj: UserObj): string {
  const userFirstName = userObj.firstname;
  const userLastName = userObj.lastname;
  const firstInitial = userFirstName.charAt(0);
  const lastInitial = userLastName.charAt(0);
  return firstInitial + lastInitial;
};

/**
 * gets a single user object from the store based on a userid
 * <p>
 * @param {Object} state - the current state of the store
 * @param {string} userId - the userId of the desired user object
 * @returns {Object} - a user object
 */
export const singleUserObjectFromUserId = function (
  state: UsersState,
  userId: string,
): UserObj | null | undefined {
  const { users } = state;
  if (users) {
    if (users[userId]) {
      return users[userId];
    }
    return null;
  }
};

/**
 * gets an array of user objects from the store based on an array of userid strings
 * <p>
 * @param {Object} state - the current state of the store
 * @param {Array} userIdsArray - array of userid strings
 * @returns {Array} - an array of user objects
 */
export const getMatchingUserObjects = function (
  state: UsersState,
  userIdsArray: string[],
): (UserObj | null | undefined)[] | null {
  if (userIdsArray) {
    const usersArray: (UserObj | null | undefined)[] = [];

    for (let u = 0; u < userIdsArray.length; u++) {
      /* skip push if userid does not exist */

      if (userIdsArray[u]) usersArray.push(singleUserObjectFromUserId(state, userIdsArray[u]));
    }
    return usersArray;
  }
  return null;
};

/**
 * gets AuthTokenKey value from local storage to be used to get authToken from cookies
 * <p>
 * @returns {string} - AuthTokenKey
 */
export const getAuthTokenKey = (): string | undefined => {
  const authTokenKey = Lockr.get('authTokenKey');
  if (authTokenKey) {
    // localStorage boundary: value is persisted as an opaque string.
    return authTokenKey as string;
  }
  return undefined;
};

// NOTE point get and set to FE utils
/**
 * gets value for a specific cookie
 * <p>
 * @param {string} key - name of the cookie
 * @returns {} - cookie value
 */
export const getAuthJwtToken = (
  key: string | undefined,
  storageType = 'localStorage',
): string | null => {
  if (!key) return null;
  const jwtCookie = Cookies.get(key);
  const jwtLocalStorage = Lockr.get(key);
  if (storageType === 'cookie' && jwtCookie) {
    return jwtCookie;
  }
  if (storageType === 'localStorage' && jwtLocalStorage) {
    // localStorage boundary: value is persisted as an opaque string.
    return jwtLocalStorage as string;
  }
  return null;
};

/**
 * sets value for a specific cookie
 * <p>
 * @param {String} key - name of the jwt token
 * @param {} value - value of the jwt token
 * @param {String} storageType - cookie or localStorage
 * @returns - cookie string in form of '<key>=<value> path=<cookie path>'
 */
export const setAuthJwtToken = (key: string, value: string, storageType = 'localStorage'): void => {
  if (storageType === 'cookie') {
    Cookies.set(key, value);
  } else {
    Lockr.set(key, value);
  }
};

/**
 * gets userid based on the set authTokenKey
 * <p>
 * @returns {string} - userId
 */
export const getCurrentUserIdFromJwt = function (): string | null {
  const authTokenKey = getAuthTokenKey();
  const token = getAuthJwtToken(authTokenKey);
  if (token) {
    return AuthUtils.getUserIdFromJWT(token);
  }
  return null;
};

/**
 * Whether the signed-in user carries the `sudo` claim.
 *
 * Distinct from utils/isSuperAdmin(), which reads ENABLE__SUPER_ADMIN - a build-time constant
 * that is false in the normal build for everyone, and so says nothing about who is signed in.
 * This reads the actual claim, which is what the backend authorises against.
 *
 * A UI affordance only: the payload is not verified here. The real boundary is the server-side
 * isSuperAdmin check on the admin endpoints.
 *
 * @returns {boolean} - true when the current JWT carries the sudo claim
 */
export const currentUserHasSudoClaim = function (): boolean {
  try {
    const token = getAuthJwtToken(getAuthTokenKey());
    return !!token && !!AuthUtils.isSuperAdmin(token);
  } catch (error) {
    return false;
  }
};

/**
 * remove jwt token from cookies / localStorage
 * <p>
 * @returns none
 */
export const removeAuthJwtToken = (): void => {
  // getAuthTokenKey may be undefined; runtime keeps prior behavior of passing it through.
  Cookies.remove(getAuthTokenKey() as string);
  Lockr.rm(getAuthTokenKey() as string);
};

// @TODO need to fake cloud-service in order to unit test
// @TODO needs to be entirely re-written into smaller functions
export const clientConfigPoll = function (
  store: AuthReduxStore,
  connectionAlert: (store: AuthReduxStore) => void,
  checkCycle: number = 5 * 60 * 1000,
): void {
  (function cycleClientConfigPoll() {
    getClientConfigAsync(serverUrl)
      .then((response) => {
        // API boundary: response is typed `unknown` by getClientConfigAsync.
        const configResponse = response as ClientConfigApiResponse | undefined;
        const blowUpStringMessage =
          'I am blowing up because something changed with respect to config values that I did not expect';
        if (configResponse && configResponse.code > 500) {
          connectionAlert(store);

          checkCycle = 2000;
        } else if (configResponse && configResponse.data) {
          dismissConnectionAlert(store);
          const obj = JSON.parse(configResponse.data);
          const { result } = obj;
          if (result) {
            const {
              currentAuthCookieName,
              env,
              flowJo11DownloadUrlMacX64,
              flowJo11DownloadUrlMacARM64,
              seqGeqDownloadUrlOsx,
              flowJoDownloadUrlOsx,
              flowJoDownloadUrlWin,
              flowJo11DownloadUrlWin,
              seqGeqDownloadUrlWin,
              portalApiVersion,
              portalCoreLibraryVersion,
              infinicytInstallerUrl,
              infinicytInstructionsUrl,
              applicationLabel,
            } = result;
            const authTokenKey = currentAuthCookieName;
            if (authTokenKey) {
              Lockr.set(authTokenKeyStorageKey, authTokenKey);
            } else {
              throw new Error(blowUpStringMessage);
            }
            Lockr.set(envStorageKey, env);
            const { clientConfigCheckCycle } = result;
            if (clientConfigCheckCycle) {
              Lockr.set(clientConfigCheckCycleStorageKey, clientConfigCheckCycle);
            } else {
              throw new Error(blowUpStringMessage);
            }
            const { userSyncPollCycle } = result;
            if (userSyncPollCycle) {
              Lockr.set(userSyncPollCycleStorageKey, userSyncPollCycle);
            } else {
              throw new Error(blowUpStringMessage);
            }
            const { applicationSyncPollCycle } = result;
            if (applicationSyncPollCycle) {
              Lockr.set(applicationSyncPollCycleStorageKey, applicationSyncPollCycle);
            } else {
              throw new Error(blowUpStringMessage);
            }
            const { china } = result;
            if (china) {
              Lockr.set('china', china);
            } else Lockr.set('china', false);

            if (china) {
              Lockr.set('seqGeqDownloadUrlOsx', seqGeqDownloadUrlOsx);
              Lockr.set('flowJoDownloadUrlOsx', flowJoDownloadUrlOsx);
              Lockr.set('flowJoDownloadUrlWin', flowJoDownloadUrlWin);
              Lockr.set('seqGeqDownloadUrlWin', seqGeqDownloadUrlWin);
              Lockr.set('flowJo11DownloadUrlMacARM64', flowJo11DownloadUrlMacARM64);
              Lockr.set('flowJo11DownloadUrlMacX64', flowJo11DownloadUrlMacX64);
              Lockr.set('flowJo11DownloadUrlWin', flowJo11DownloadUrlWin);
            } else {
              Lockr.set('flowJo11DownloadUrlMacX64', null);
              Lockr.set('flowJo11DownloadUrlMacX64', null);
              Lockr.set('seqGeqDownloadUrlOsx', null);
              Lockr.set('flowJoDownloadUrlOsx', null);
              Lockr.set('flowJoDownloadUrlWin', null);
              Lockr.set('seqGeqDownloadUrlWin', null);
              Lockr.set('flowJo11DownloadUrlOsx', null);
              Lockr.set('flowJo11DownloadUrlWin', null);
            }
            if (portalApiVersion) {
              setValueInLocalStorage('portalApiVersion', portalApiVersion);
            } else {
              throw new Error(blowUpStringMessage);
            }
            if (portalCoreLibraryVersion) {
              setValueInLocalStorage('portalCoreLibraryVersion', portalCoreLibraryVersion);
            } else {
              throw new Error(blowUpStringMessage);
            }

            if (infinicytInstallerUrl) {
              Lockr.set('infinicytInstallerUrl', infinicytInstallerUrl);
            } else {
              throw new Error(blowUpStringMessage);
            }
            if (infinicytInstructionsUrl) {
              Lockr.set('infinicytInstructionsUrl', infinicytInstructionsUrl);
            } else {
              throw new Error(blowUpStringMessage);
            }
            if (applicationLabel) {
              Lockr.set('applicationLabel', applicationLabel);
            } else {
              throw new Error(blowUpStringMessage);
            }

            checkCycle = result.clientConfigCheckCycle;
          } else {
            console.log('problem with clientConfig result', result);

            checkCycle = 2000;
          }
        }
      })
      .then(() => {
        setTimeout(cycleClientConfigPoll, checkCycle);
      })
      .catch((err) => {
        console.log('Error is: ', err);
      });
  })();
};

/**
 * processes email verification toke if exists in url parameter,
 * else authenticates user with jwt token if token is available
 * <p>
 * @param {Object} store - redux store
 * @returns {bool} - whether or not the authentication request was made
 */
export const initiateSyncPolling = function (store: AuthReduxStore): boolean | undefined {
  const verificationToken = AuthClientSelectors.getEmailVerificationTokenState(false);
  if (verificationToken) {
    const emailVerificationStore = store.getState().emailVerification;
    const userId = AuthClientSelectors.getUserIdState(emailVerificationStore);
    const emailAddress = AuthClientSelectors.getEmailAddressState(emailVerificationStore);
    if (userId && emailAddress) {
      const action = CloudActions.emailVerificationTokenVerify(
        userId,
        emailAddress,
        AuthClientSelectors.getEmailVerificationTokenState(false),
      );
      store.dispatch(action);
      return;
    }
    // this is probably the phantom case:
    return;
  }
  const authToken = getAuthJwtToken(getAuthTokenKey());
  if (authToken) {
    const action = CloudActions.startCloudWatcher();
    store.dispatch(action);
    return true;
  }
};

/**
 * makes request to cloud with an AuthToken
 * <p>
 * @param {string} authToken - JWT to make request with
 * @returns {Object} - response from CloudActions.requestWithAuthToken
 */
export const requestWithAuthToken = function (authToken: string): unknown {
  // `requestWithAuthToken` is absent from cloud-core's typed surface; preserve the original JS call.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped cloud-core member
  return (CloudActions as any).requestWithAuthToken(authToken);
};

/**
 * set stopUserSync in local storage to true
 * <p>
 * @returns - none
 */
export const stopUserSync = function (): void {
  Lockr.set('stopUserSync', true);
};

/**
 * set stopUserSync in local storage to null
 * <p>
 * @returns - none
 */
export const restartUserSync = function (): void {
  Lockr.set('stopUserSync', null);
};

/**
 * get applicationSyncPollCycle from local storage
 * <p>
 * @returns applicationSyncPollCycle value
 */
export const applicationSyncPollCycle = function (): unknown {
  return Lockr.get('applicationSyncPollCycle');
};

/**
 * get userSyncPollCycle from local storage
 * <p>
 * @returns userSyncPollCycle value
 */
export const userSyncPollCycle = function (): unknown {
  return Lockr.get('userSyncPollCycle');
};

/**
 * get lastUserActiveTimestamp from local storage
 * <p>
 * @returns lastUserActiveTimestamp value
 */
export const getUserLastActiveTimestamp = function (): unknown {
  return Lockr.get('lastUserActiveTimestamp');
};

/**
 * get difference between api timestamp and client timestamp based on local storage values
 * <p>
 * @returns {int} time client is off by
 */
export const clientToApiTimestampDiff = function (): number {
  // localStorage boundary: timestamps are persisted as numbers.
  const theApiTimestamp = Lockr.get('apiTimestamp') as number;
  const theClientTimestamp = Lockr.get('clientTimestamp') as number;
  const clientOffBy = theApiTimestamp - theClientTimestamp;
  return clientOffBy;
};

export const getAuthToken = (): string | null => getAuthJwtToken(getAuthTokenKey());

// @TODO Refactor and modularize the client config mechanism built into auth-client (saves values
// to localStorage) such that when the last key:value pair is saved from the API request, we add a
// config: true type of boolean
export const isClientConfigReady = (): boolean => !!applicationSyncPollCycle();
